import { batch } from "react-redux";
import type { AddToCartSource } from "~/app/analytics/types";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { getStore } from "~/app/store/getStore";
import { TranslationType } from "~/common/types/TranslationModel";
import { format, formatNullable } from "~/common/utils/ApiHelper";
import { openModal } from "~/common/utils/ModalService";
import { userIsSalesman } from "~/domains/user/utils/roles";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { addQuantity } from "~/features/addToCart/redux/cartAmounts/actions";
import { sendAddToQuote, type AddToQuoteApiItem } from "~/features/cart/activeQuote/api/addToQuote";
import {
  sendAddToCart,
  type AddToCartApiItem,
  type AddToCartPartApiItem,
  type AddToCartResponseItem,
} from "~/features/cart/cart/api/AddToCart";
import { clearCart } from "~/features/cart/cart/redux/actions/cartPageActions";
import { mapAddToCartResponseItemsToAnalyticsProducts } from "~/features/cart/cart/utils/addToCartResponseItems";
import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useTranslation } from "react-i18next";
import { Snackbar } from "~/common/mui";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";

export interface AddToCartPartItem {
  productCode: string;
  quantity: number;
  packageCode: string;
  packageSize: number;
  primaryWarehouseCode: string;
  partTypeCode: string;
  manualUpdated: boolean;
  originCountry: string;
}

export interface AddToCartItem {
  productCode: string;
  packageSize?: number;
  packageCode?: string;
  quantity: number;
  comment?: string;
  internalComment?: string;
  selectedDeliveryDate?: Date | null;
  selectedDeliveryDateIsManual?: boolean;
  primaryWarehouseCode?: string;
  contractNumber?: string;
  contractLine?: string;
  contractOpenQuantity?: number;
  priceReference?: string;
  position?: number;
  quoteLine?: string;
  quoteNumber?: string;
  quoteScheduleLineNum?: string;
  quoteMinQuantity?: number;
  isBundled?: boolean;
  quoteExpiryDate?: Date | null;
  logisticalComment?: string;
  estimatedShippingDate?: Date | null;
  restrictedPackage?: boolean;
  optionalParts?: AddToCartPartItem[];
  customerOrderNumber?: string;
  originCountry?: string;
  salesCampaignCode?: string;
  offerPrice?: number;
  targetPrice?: number;
  msp?: number;
  rsp?: number;
  recommendationType?: string;
  recommendationPlace?: string;
}

export type AddToCartSuccessUi = "modal" | "toast" | "none";
export type AddToCartErrorUi = "toast" | "none";

export interface AddToCartOptions {
  items: AddToCartItem[];
  page: PurchasePlace;
  source?: AddToCartSource;
  recommendationType?: PurchaseType;
  recommendationPlace?: PurchasePlace;
  successUi?: AddToCartSuccessUi;
  errorUi?: AddToCartErrorUi;
  overrideCart?: boolean;
  skipCartContentTracking?: boolean;
}

export async function addToCart({
  items,
  page,
  source = "unknown",
  recommendationType,
  recommendationPlace,
  successUi,
  errorUi = "toast",
  overrideCart = false,
  skipCartContentTracking = false,
}: AddToCartOptions): Promise<AddToCartResponseItem[]> {
  const dispatch = getStore().dispatch;
  const isSalesman = userIsSalesman();
  const effectiveSuccessUi = successUi ?? (isSalesman ? "toast" : "modal");

  try {
    if (overrideCart) {
      await dispatch(clearCart());
    }

    const responseItems = isSalesman
      ? await addFlowItemsToQuote(items, { recommendationType, recommendationPlace })
      : await sendAddToCart(items.map(mapAddToCartItemToCartApiItem), skipCartContentTracking);

    updateCartAmounts(responseItems);

    if (!isSalesman) {
      trackAddToCartResponse(responseItems, { source, recommendationType, recommendationPlace });
    }

    showSuccessUi(effectiveSuccessUi, {
      page,
      responseItems,
      initialCount: totalQuantity(items),
      isSalesman,
    });

    return responseItems;
  } catch (error) {
    if (errorUi === "toast") {
      dispatch(
        addMessageAdvanced("error", {
          type: TranslationType.INTERNAL,
          value: isSalesman ? "addToQuote.failure" : "addToCart.failure",
          params: { count: totalQuantity(items) },
        }),
      );
    }
    throw error;
  }
}

export function mapAddToCartItemToCartApiItem(item: AddToCartItem): AddToCartApiItem {
  return {
    productCode: item.productCode,
    packageSize: item.packageSize,
    packageCode: item.packageCode ?? INVALID_PACKAGE_CODE,
    quantity: item.quantity,
    comment: item.comment,
    selectedDeliveryDate: formatNullable(item.selectedDeliveryDate ?? null),
    selectedDeliveryDateIsManual: item.selectedDeliveryDateIsManual ?? !!item.selectedDeliveryDate,
    primaryWarehouseCode: item.primaryWarehouseCode ?? "",
    contractNumber: item.contractNumber,
    contractLine: item.contractLine,
    contractOpenQuantity: item.contractOpenQuantity,
    priceReference: item.priceReference ?? "",
    quoteLine: item.quoteLine,
    quoteNumber: item.quoteNumber,
    quoteScheduleLineNum: item.quoteScheduleLineNum,
    quoteMinQuantity: item.quoteMinQuantity,
    isBundled: item.isBundled,
    quoteExpiryDate: formatNullable(item.quoteExpiryDate),
    logisticalComment: item.logisticalComment,
    estimatedShippingDate: formatNullable(item.estimatedShippingDate),
    isRestrictedPackage: item.restrictedPackage ?? false,
    orderEntryNumber: item.position,
    optionalParts: item.optionalParts?.map(mapAddToCartPartItemToCartPartApiItem),
    customerOrderNumber: item.customerOrderNumber,
    originCountry: item.originCountry ?? "",
    salesCampaignCode: item.salesCampaignCode,
  };
}

export function mapAddToCartItemToQuoteApiItem(item: AddToCartItem): AddToQuoteApiItem {
  return {
    productCode: item.productCode,
    packageSize: item.packageSize,
    packageCode: item.packageCode ?? INVALID_PACKAGE_CODE,
    quantity: item.quantity,
    comment: item.comment,
    internalComment: item.internalComment ? item.internalComment + "\n" : "",
    selectedDeliveryDate: format(item.selectedDeliveryDate ?? new Date()),
    primaryWarehouseCode: item.primaryWarehouseCode ?? "",
    offerPrice: item.offerPrice,
    targetPrice: item.targetPrice,
    minimumSellingPrice: item.msp,
    recommendedSellingPrice: item.rsp,
    priceReference: item.priceReference ?? "",
  };
}

function mapAddToCartPartItemToCartPartApiItem(item: AddToCartPartItem): AddToCartPartApiItem {
  return {
    productCode: item.productCode,
    quantity: item.quantity,
    packageCode: item.packageCode,
    packageSize: item.packageSize,
    primaryWarehouseCode: item.primaryWarehouseCode,
    partTypeCode: item.partTypeCode,
    manualUpdated: item.manualUpdated,
    originCountry: item.originCountry,
  };
}

async function addFlowItemsToQuote(
  items: AddToCartItem[],
  context: { recommendationType?: PurchaseType; recommendationPlace?: PurchasePlace },
): Promise<AddToCartResponseItem[]> {
  const response = await sendAddToQuote(items.map(mapAddToCartItemToQuoteApiItem), context);
  return readQuoteResponseItems(response);
}

async function readQuoteResponseItems(response: Response): Promise<AddToCartResponseItem[]> {
  const body = await response.json();
  if (!Array.isArray(body) || !body.every(isAddToCartResponseItem)) {
    throw new Error("Quote add-to-cart returned an invalid response");
  }
  return body;
}

function isAddToCartResponseItem(item: unknown): item is AddToCartResponseItem {
  return (
    typeof item === "object" &&
    item !== null &&
    typeof (item as AddToCartResponseItem).pk === "number" &&
    typeof (item as AddToCartResponseItem).productCode === "string" &&
    typeof (item as AddToCartResponseItem).packCode === "string" &&
    typeof (item as AddToCartResponseItem).quantity === "number"
  );
}

function updateCartAmounts(responseItems: AddToCartResponseItem[]) {
  const dispatch = getStore().dispatch;

  batch(() => {
    responseItems.forEach((item) => {
      dispatch(addQuantity(item.productCode, item.packCode, item.quantity, item.priceReference ?? ""));
    });
  });
}

function showSuccessUi(
  successUi: AddToCartSuccessUi,
  {
    page,
    responseItems,
    initialCount,
    isSalesman,
  }: { page: PurchasePlace; responseItems: AddToCartResponseItem[]; initialCount: number; isSalesman: boolean },
) {
  const dispatch = getStore().dispatch;

  if (successUi === "modal") {
    const resultCount = totalQuantity(responseItems);
    const quantityAdjusted = resultCount !== initialCount;

    // No better place to show it right now..
    if (quantityAdjusted && isPhaseOut2Enabled() && page === "WISHLIST") {
      openModal("qty-adjusted-snackbar", (close) => <QtyAdjustedSnackbar close={close} />);
    }

    openModal("add-to-cart-dialog", (close) => (
      <CartSuccessModal onClose={close} page={page} responseItems={responseItems} />
    ));
  }

  if (successUi === "toast") {
    dispatch(
      addMessageAdvanced("info", {
        type: TranslationType.INTERNAL,
        value: isSalesman ? "addToQuote.success" : "addToCart.success",
        params: { count: initialCount },
      }),
    );
  }
}

function trackAddToCartResponse(
  responseItems: AddToCartResponseItem[],
  context: {
    source: AddToCartSource;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
) {
  if (!responseItems.length) {
    return;
  }

  const isRecommendation = !!context.recommendationType && !!context.recommendationPlace;

  AnalyticsMQ.instance.push("addToCart", {
    products: mapAddToCartResponseItemsToAnalyticsProducts(responseItems),
    context: {
      source: context.source,
      recommendationPlace: isRecommendation ? context.recommendationPlace : undefined,
      recommendationType: isRecommendation ? context.recommendationType : undefined,
    },
  });
}

function totalQuantity(items: { quantity: number }[]) {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

const QtyAdjustedSnackbar = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();

  return (
    <Snackbar
      open={true}
      message={t("product.phaseout.qtyAdjusted.past", "Your quantity has been adjusted")}
      onClose={close}
      action={<SnackbarCloseButton onClick={close} />}
    />
  );
};
